import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import { EditEmployeeContractForm } from '@/components/features/employee/editEmployeeContractForm';
import { useEmployeeContractEdit } from '@/hooks/employee/useEmployeeContractEdit';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { buildEmployeeFullName, formatEmployeeIdentity } from '@/utils';


export const EmployeeContractEdit: React.FC = () => {
  const { t: translate } = useTranslation();
  const { employeeId, contractId } = useParams<{
    employeeId: string;
    contractId: string;
  }>();

  const {
    isLoading,
    updating,
    error,
    employeeProfile,
    handleCancel,
    handleUpdate,
    contractStatusOptions,
    contractTypeOptions,
    contractModalityOptions,
    companyOptions,
    areaOptions,
    positionOptions,
    costCenterOptions,
    unionOptions,
    form,
  } = useEmployeeContractEdit(
    employeeId ? Number(employeeId) : undefined,
    contractId ? Number(contractId) : undefined,
  );

  // Build backTo URL with employeeId query parameter
  const backToUrl = useMemo(() => {
    const baseUrl = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS.path;
    return employeeId ? `${baseUrl}?employeeId=${employeeId}` : baseUrl;
  }, [employeeId]);
  
  // Build dynamic title with employee name and RUT
  const dynamicTitle = useMemo(() => {
    const identity = formatEmployeeIdentity(
      buildEmployeeFullName(employeeProfile ?? {}),
      employeeProfile?.rutFull,
    );
    return identity ? translate('employee:breadcrumb.contractDetail', { identity }) : undefined;
  }, [employeeProfile, translate]);

  if (error) {
    return (
      <PageLayout backTo={backToUrl} dynamicTitle={dynamicTitle}>
        <div className="flex flex-col gap-4 p-4" data-testid="employee-contract-edit-error">
          <div className="text-red-600 text-sm">{error}</div>
          <div>
            <PrimaryButton variant="outline" onClick={handleCancel}>
              {translate('common:actions.backToList')}
            </PrimaryButton>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout backTo={backToUrl} dynamicTitle={dynamicTitle} isLoading={isLoading}>
      <div data-testid="employee-contract-edit-page">
        <EditEmployeeContractForm
          contractForm={form}
          contractStatusOptions={contractStatusOptions}
          contractTypeOptions={contractTypeOptions}
          contractModalityOptions={contractModalityOptions}
          companyOptions={companyOptions}
          areaOptions={areaOptions}
          positionOptions={positionOptions}
          costCenterOptions={costCenterOptions}
          unionOptions={unionOptions}
          updating={updating}
          onCancel={handleCancel}
          onSave={handleUpdate}
        />
      </div>
    </PageLayout>
  );
};

export default EmployeeContractEdit;
